/**
 * Finishing a purchase that was paid for before the account existed.
 *
 * The same two lines are needed in five places — the page the pack was bought
 * on, /claim/<token> and /download/<token> from the e-mail, /interview-prep/<token>
 * saving a prep onto an account, and /auth/callback after signing up with
 * Google — so the call and the "where do they land" decision live here.
 */
export function useGuestClaim() {
  const { takePendingClaimToken, hasPendingPosting, hasPendingInterview } = usePendingPurchase()

  type ClaimPlan = 'one-time' | 'tailored-pdf' | 'job-pack' | 'interview-prep'

  interface ClaimResult {
    cvId: string
    plan: ClaimPlan
    /** A tailored CV bought from the editor: its tailoring opens there, to apply. */
    tailoring: boolean
  }

  /** Puts the purchase on the signed-in account; returns the CV it opened (or the prep went with). */
  async function claimPack(token: string): Promise<ClaimResult | null> {
    const response = await $fetch<{ success: boolean; cvId: string | null; plan: ClaimPlan; tailoring?: boolean }>(
      '/api/guest-export/claim',
      { method: 'POST', body: { token } },
    )
    return response.success && response.cvId ? { cvId: response.cvId, plan: response.plan, tailoring: !!response.tailoring } : null
  }

  /** Claims and opens the CV. Returns false when the claim did not go through. */
  async function claimAndOpen(token: string): Promise<boolean> {
    try {
      const claimed = await claimPack(token)
      if (!claimed) return false
      // An interview prep goes back to its own page, which now says where it was kept.
      if (claimed.plan === 'interview-prep') {
        await navigateTo(`/interview-prep/${token}`)
        return true
      }
      // Bought from the editor's preview: back to that CV with the tailoring open.
      if (claimed.tailoring) {
        await navigateTo(`/cv/${claimed.cvId}?section=tailor&tailoring=${token}`)
        return true
      }
      // A tailored PDF bought under a new CV: the editor opens on it with every
      // change listed against the CV it was written from, to find, change or undo.
      if (claimed.plan === 'tailored-pdf') {
        await navigateTo(`/cv/${claimed.cvId}?export_paid=true&section=tailor&changes=${token}`)
        return true
      }
      // The CV page waits for what was bought to land: Premium for a pack, the
      // export credit for a single PDF.
      const flag = claimed.plan === 'job-pack' ? 'pack_paid=true' : 'export_paid=true'
      // A pack bought after comparing the CV with a posting opens on tailoring to it.
      // A posting tailored to, or an interview prep previewed, before the pack:
      // the editor opens where that work continues.
      const section = claimed.plan === 'job-pack' && (hasPendingPosting() || hasPendingInterview()) ? '&section=tailor' : ''
      await navigateTo(`/cv/${claimed.cvId}?${flag}${section}`)
      return true
    } catch (err) {
      console.error('Could not claim the purchase:', err)
      return false
    }
  }

  /**
   * For pages that people arrive at with a session in hand (/auth/callback):
   * finishes a purchase parked before the sign-up, if there is one.
   */
  async function claimPendingPack(): Promise<boolean> {
    const token = takePendingClaimToken()
    if (!token) return false
    return await claimAndOpen(token)
  }

  return { claimPack, claimAndOpen, claimPendingPack }
}
